import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import AppEnvironment from '../../components/enums/appEnvironment.enum';
import api, { restApi } from '../../services/api';
import { clearFederatedSessionFlag } from '../../services/modules/sso/sso.utils';
import { useAppEnvironmentStore } from '../../stores/AppEnvironment/appEnvironment.store';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import useProductActionsStore from '../../stores/ProductActionsStore/productActions.store';
import useProductDetailsStore from '../../stores/ProductDetails/productDetails.store';
import { TeceoApiResponse } from '../../types/request.types';
import { updateTeamIdInSW } from '../../utils/serviceWorker/utils/serviceWorkerSharedData.util';
import { StorageEnum } from '../context/storage';
import myNetworkStore from '../my-network/myNetwork.store';
import { resetRachelSession } from '../rachel/rachelSession.utils';
import { useAbility } from '../users/AbilityContext';
import { isContractSignPath } from './authContext.utils';

export type UserMainStorage = {
  [StorageEnum.SESSION_ID]: string;
  [StorageEnum.TEAM_ID]: string;
  [StorageEnum.ADMIN_TEAM_ID]: string;
  [StorageEnum.BRAND_ID]: string;
  [StorageEnum.APP_ENVIRONMENT]: AppEnvironment;
};

type AuthContextProviderProps = {
  children: React.ReactNode;
};

export type LoginContext = {
  id: string;
  slug: string;
  bannerUrl: string;
  loginImageUrl?: string | null;
  enablePublicShowroom?: boolean;
  enableMarkup?: boolean;
  enableRetailPrice?: boolean;
  brandName: string;
  customerSupportPhone?: string | null;
  hasFederatedLogin?: boolean;
};

export interface AuthContextProps {
  user?: UserMainStorage;
  loggedin: boolean;
  redirectPath: string;
  logout: (options?: { keepLoginContext: boolean }) => Promise<void>;
  forceLogout: () => void;

  handleUserLogin: (user: UserMainStorage) => void;
  handleSetLogin: (data: LoginContext) => void;
  loading: boolean;
  loginContext?: LoginContext;
  initContext: () => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextProps>({
  loggedin: false,
  redirectPath: '/',
  user: undefined,
  loading: false,
  handleUserLogin: () => {
    return;
  },
  handleSetLogin: () => {
    return;
  },
  forceLogout: () => {
    return;
  },
  logout: () => Promise.resolve(),
  loginContext: undefined,
  initContext: () => Promise.resolve(),
});

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const queryClient = useQueryClient();

  const [user, setUser] = useState<UserMainStorage>();
  const [loggedin, setLoggedin] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/');
  const [loading, setLoading] = useState(true);
  const [loginContext, setLoginContext] = useState<LoginContext>();
  const { resetAppEnvironment } = useAppEnvironmentStore(
    useShallow((state) => ({
      resetAppEnvironment: state.resetAppEnvironment,
    })),
  );

  const resetProductActionsStore = useProductActionsStore(
    (state) => state.resetProductActionsStore,
  );
  const resetFilterWithOrders = useProductDetailsStore(
    (filterState) => filterState.resetFilterWithOrders,
  );
  const { reset: resetNetworkStore } = myNetworkStore;
  const resetStore = useOrderBookStore((state) => state.resetStore);

  const { getAbilities } = useAbility();

  const withUser = useCallback(
    async (localUser: UserMainStorage) => {
      api.setToken(localUser[StorageEnum.SESSION_ID]);
      api.setTeam(localUser[StorageEnum.TEAM_ID]);
      api.setAppEnvironment(localUser[StorageEnum.APP_ENVIRONMENT]);

      if (localUser[StorageEnum.ADMIN_TEAM_ID]) {
        api.setAdminTeam(localUser[StorageEnum.ADMIN_TEAM_ID]);
      }

      if (localUser[StorageEnum.BRAND_ID]) {
        api.setBrandContext(localUser[StorageEnum.BRAND_ID]);
      }

      await getAbilities();
    },
    [getAbilities],
  );

  const handleUserLogin = useCallback(
    async (localUser: UserMainStorage) => {
      try {
        await withUser(localUser);
        localStorage.user = JSON.stringify(localUser);
        setUser(localUser);
        setLoggedin(true);
      } catch (_) {
        await logout();
      } finally {
        setLoading(false);
      }
    },
    [withUser],
  );

  const handleSetLogin = (data: LoginContext) => {
    setLoginContext(data);
  };

  const handleUserNotLoggedIn = useCallback(async () => {
    const { pathname, searchParams } = new URL(window.location.href);

    const splittedPathname = pathname.split('/');
    const brandSlug = splittedPathname[1] === 'events' ? splittedPathname[2] : splittedPathname[1];

    const baseUrl = splittedPathname[1] === 'events' ? splittedPathname[1] : 'brands';

    const handleLoginContextError = () => {
      setRedirectPath(searchParams.get('redirectPath') || pathname || '/');
      setLoginContext(undefined);
      setLoading(false);
    };

    if (isContractSignPath({ pathname })) {
      handleLoginContextError();
      return;
    }

    const response: TeceoApiResponse<LoginContext> = await restApi.get(
      `${baseUrl}/${brandSlug}/login-context`,
    );

    if (response.ok) {
      setLoginContext(response.data);
      setRedirectPath(searchParams.get('redirectPath') || `/${response.data?.slug || ''}`);
      setLoading(false);
      return;
    }

    handleLoginContextError();
  }, []);

  const logout = useCallback(async (options?: { keepLoginContext: boolean }) => {
    api.resetSessionHeaders();
    resetFilterWithOrders();

    queryClient.clear();

    resetProductActionsStore();

    delete localStorage.user;
    setUser(undefined);
    setRedirectPath('/');
    setLoggedin(false);
    resetNetworkStore();
    resetRachelSession();
    resetAppEnvironment();

    clearFederatedSessionFlag();

    await api.logout();
    if (!options?.keepLoginContext) {
      setLoginContext(undefined);
    }
  }, []);

  const forceLogout = useCallback(() => {
    clearFederatedSessionFlag();
    api.resetSessionHeaders();
    resetStore();
    resetFilterWithOrders();
    resetNetworkStore();
    resetRachelSession();
    queryClient.clear();
    delete localStorage.user;
    setUser(undefined);
    setLoggedin(false);
  }, [resetStore]);

  const initContext = useCallback(async () => {
    const stringUser = localStorage.user;
    if (stringUser) {
      await handleUserLogin(JSON.parse(stringUser));
      return;
    }
    await handleUserNotLoggedIn();
  }, [handleUserLogin, handleUserNotLoggedIn]);

  useEffect(() => {
    initContext();
  }, [initContext]);

  useEffect(() => {
    updateTeamIdInSW(user?.[StorageEnum.TEAM_ID] || '');
  }, [user?.[StorageEnum.TEAM_ID]]);

  return (
    <AuthContext.Provider
      value={{
        loggedin,
        redirectPath,
        logout,
        user,
        handleUserLogin,
        loading,
        handleSetLogin,
        loginContext,
        initContext,
        forceLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext<AuthContextProps>(AuthContext);
